import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { clients as clientsApi, announcements } from '../api/client'
import PageHeader from '../components/PageHeader'
import { PageLayout, Card, TextArea, Button, ErrorMessage, Checkbox } from '../components/ui'

export default function AnnouncementsPage() {
  const queryClient = useQueryClient()
  const [message, setMessage] = useState('')
  const [targetType, setTargetType] = useState('all')
  const [selectedClientIds, setSelectedClientIds] = useState([])
  const [postToChat, setPostToChat] = useState(false)
  const [selectedUpdates, setSelectedUpdates] = useState([])
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  // Fetch release logs for auto-fill
  const { data: releaseLogs } = useQuery({
    queryKey: ['releaseLogs'],
    queryFn: () => announcements.getReleaseLogs(),
  })

  // Fetch clients for targeted sending
  const { data: clientList } = useQuery({
    queryKey: ['clients'],
    queryFn: () => clientsApi.list(),
  })

  // Fetch past announcements
  const { data: pastAnnouncements } = useQuery({
    queryKey: ['announcements'],
    queryFn: () => announcements.list(),
  })

  const sendMutation = useMutation({
    mutationFn: (data) => announcements.create(data),
    onSuccess: (data) => {
      setSuccess(`Sent to ${data.recipientCount} client${data.recipientCount !== 1 ? 's' : ''}!`)
      setMessage('')
      setSelectedUpdates([])
      setPostToChat(false)
      queryClient.invalidateQueries({ queryKey: ['announcements'] })
    },
    onError: (err) => {
      setError(err.message || 'Failed to send announcement')
    },
  })

  function toggleUpdate(update) {
    setSelectedUpdates(prev => {
      const exists = prev.find(u => u.id === update.id)
      if (exists) {
        return prev.filter(u => u.id !== update.id)
      }
      return [...prev, update]
    })
  }

  function selectAllUpdates() {
    if (!releaseLogs?.releases) return
    if (selectedUpdates.length === releaseLogs.releases.length) {
      setSelectedUpdates([])
    } else {
      setSelectedUpdates([...releaseLogs.releases])
    }
  }

  function toggleClient(clientId) {
    setSelectedClientIds(prev =>
      prev.includes(clientId) ? prev.filter(id => id !== clientId) : [...prev, clientId]
    )
  }

  function handleSend() {
    setError('')
    setSuccess('')

    // Build the full message with optional updates
    let fullMessage = message.trim()
    if (selectedUpdates.length > 0) {
      const updateLines = selectedUpdates.map(u =>
        `- ${u.title || u.version}: ${u.description || ''}`
      ).join('\n')
      if (fullMessage) {
        fullMessage += '\n\n--- Updates ---\n' + updateLines
      } else {
        fullMessage = updateLines
      }
    }

    if (!fullMessage) {
      setError('Write a message or select at least one update to include.')
      return
    }

    sendMutation.mutate({
      message: fullMessage,
      targetType,
      clientIds: targetType === 'selected' ? selectedClientIds : undefined,
      postToChat,
      includedUpdates: selectedUpdates.length > 0 ? selectedUpdates.map(u => ({
        id: u.id,
        version: u.version,
        title: u.title,
      })) : undefined,
    })
  }

  const activeClients = (Array.isArray(clientList) ? clientList : []).filter(c => c.isActive !== false)

  return (
    <PageLayout>
      <PageHeader title="Broadcast" showBack />

      <div className="px-4 py-4 space-y-4">
        <ErrorMessage error={error} />

        {success && (
          <div className="bg-lume-teal/10 border border-lume-teal/30 rounded-lg p-3 text-lume-teal text-sm">
            {success}
          </div>
        )}

        {/* Message Composer */}
        <Card>
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-indices-bright">Your Message</h3>
            <TextArea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              placeholder="Write your message to clients..."
            />
          </div>
        </Card>

        {/* Auto-fill from Release Log */}
        {releaseLogs?.releases?.length > 0 && (
          <Card>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-indices-bright">Include Updates</h3>
                <button
                  onClick={selectAllUpdates}
                  className="text-xs text-lume-teal hover:text-lume-green"
                >
                  {selectedUpdates.length === releaseLogs.releases.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              <p className="text-xs text-indices-muted">Pick which recent updates to include in your message.</p>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {releaseLogs.releases.slice(0, 10).map(release => (
                  <label
                    key={release.id}
                    className={`flex items-start gap-3 p-2 rounded-lg cursor-pointer border transition-colors ${
                      selectedUpdates.find(u => u.id === release.id)
                        ? 'border-lume-teal/50 bg-lume-teal/5'
                        : 'border-dial-border hover:border-indices-muted'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={!!selectedUpdates.find(u => u.id === release.id)}
                      onChange={() => toggleUpdate(release)}
                      className="mt-0.5 accent-lume-teal"
                    />
                    <div className="min-w-0">
                      <div className="text-sm text-indices-bright font-medium">
                        {release.title || release.version}
                      </div>
                      {release.description && (
                        <div className="text-xs text-indices-muted line-clamp-2 mt-0.5">
                          {release.description}
                        </div>
                      )}
                      <div className="text-xs text-indices-muted mt-1">
                        {release.releaseType} &middot; {new Date(release.releasedAt || release.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </Card>
        )}

        {/* Target Audience */}
        <Card>
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-indices-bright">Send To</h3>
            <div className="flex gap-2">
              <button
                onClick={() => setTargetType('all')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  targetType === 'all'
                    ? 'bg-lume-teal text-dial-black'
                    : 'bg-dial-elevated text-indices-medium hover:text-indices-bright'
                }`}
              >
                All Clients ({activeClients.length})
              </button>
              <button
                onClick={() => setTargetType('selected')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  targetType === 'selected'
                    ? 'bg-lume-teal text-dial-black'
                    : 'bg-dial-elevated text-indices-medium hover:text-indices-bright'
                }`}
              >
                Select Clients
              </button>
            </div>

            {targetType === 'selected' && (
              <div className="space-y-2 max-h-48 overflow-y-auto mt-2">
                {activeClients.map(client => (
                  <label key={client.id} className="flex items-center gap-3 p-2 rounded hover:bg-dial-elevated cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedClientIds.includes(client.id)}
                      onChange={() => toggleClient(client.id)}
                      className="accent-lume-teal"
                    />
                    <span className="text-sm text-indices-bright">
                      {client.firstName} {client.lastName}
                    </span>
                  </label>
                ))}
                {activeClients.length === 0 && (
                  <p className="text-sm text-indices-muted">No active clients</p>
                )}
              </div>
            )}
          </div>
        </Card>

        {/* Options */}
        <Card>
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-indices-bright">Options</h3>
            <Checkbox
              label="Also post in AI chat (appears as a message from you)"
              checked={postToChat}
              onChange={(e) => setPostToChat(e.target.checked)}
            />
          </div>
        </Card>

        {/* Send Button */}
        <Button
          onClick={handleSend}
          fullWidth
          loading={sendMutation.isPending}
          disabled={!message.trim() && selectedUpdates.length === 0}
        >
          Send Broadcast
        </Button>

        {/* Past Announcements */}
        {pastAnnouncements?.announcements?.length > 0 && (
          <Card>
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-indices-bright">Past Broadcasts</h3>
              <div className="space-y-3">
                {pastAnnouncements.announcements.map(ann => (
                  <div key={ann.id} className="border-b border-dial-border pb-3 last:border-0 last:pb-0">
                    <p className="text-sm text-indices-bright whitespace-pre-wrap line-clamp-3">
                      {ann.message}
                    </p>
                    <div className="flex items-center gap-3 mt-1.5 text-xs text-indices-muted">
                      <span>{new Date(ann.createdAt).toLocaleDateString()}</span>
                      <span>&middot;</span>
                      <span>{ann.readCount}/{ann.recipientCount} read</span>
                      <span>&middot;</span>
                      <span>{ann.targetType === 'all' ? 'All clients' : 'Selected'}</span>
                      {ann.postToChat && (
                        <>
                          <span>&middot;</span>
                          <span className="text-lume-teal">Chat</span>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        )}
      </div>
    </PageLayout>
  )
}
